import { Link } from 'react-router-dom';
import { usePageMeta } from '../lib/usePageMeta.js';

export function HowItWorksPage() {
  usePageMeta({
    title: 'How TowShare works — peer-to-peer trailer rental',
    description:
      'How TowShare works for renters and owners: search nearby trailers, verify ID, pay through Stripe, pick up, return. Backed by the $1,500 Trailer Guarantee.',
    canonicalPath: '/how-it-works',
  });
  return (
    <div>
      <section className="bg-gradient-to-br from-navy via-navy-600 to-navy-700 text-white">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center">
          <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-3">
            How it works
          </p>
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Trailers, on demand.</h1>
          <p className="text-lg text-navy-100 max-w-2xl mx-auto">
            Same-day rentals. Local owners. Lower prices than the rental yard.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-16">
        <h2 className="text-2xl font-bold text-navy mb-8">For renters</h2>
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          <Step
            n="1"
            title="Search"
            body="Find trailers near you by type, size, and date."
          />
          <Step
            n="2"
            title="Book"
            body="Send a request — or instant-book listings with one tap."
          />
          <Step
            n="3"
            title="Pick up"
            body="Take photos at handoff with the owner. We hold a deposit on your card."
          />
          <Step
            n="4"
            title="Return"
            body="Bring it back in good shape. Deposit is released in 24 hours."
          />
        </div>

        <h2 className="text-2xl font-bold text-navy mb-8">For owners</h2>
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          <Step
            n="1"
            title="List"
            body="Photos, price, pickup location. 5 minutes."
          />
          <Step
            n="2"
            title="Connect Stripe"
            body="So we know where to send your earnings — automatic, 2–3 business days."
          />
          <Step
            n="3"
            title="Approve renters"
            body="See who's asking, check reviews, accept or decline."
          />
          <Step
            n="4"
            title="Get paid"
            body="Renter pays through Stripe. We take 10%, you get the rest."
          />
        </div>

        <div className="card p-6 bg-orange-50/50 border-orange-100">
          <h3 className="text-lg font-semibold text-navy mb-2">
            Built-in protection on every rental
          </h3>
          <ul className="text-navy-700 space-y-1 mb-4">
            <li>✓ Stripe Identity verification before first booking</li>
            <li>✓ Refundable damage deposit pre-authorized at checkout</li>
            <li>✓ $1,500 Trailer Guarantee on top of renter's auto insurance</li>
            <li>✓ Mutual photo handoff at pickup and return</li>
            <li>✓ Mediated dispute support from TowShare</li>
          </ul>
          <Link to="/guarantee" className="text-orange font-medium hover:underline">
            Read about the Guarantee →
          </Link>
        </div>
      </section>
    </div>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div>
      <div className="w-10 h-10 rounded-full bg-orange text-white font-bold flex items-center justify-center mb-3">
        {n}
      </div>
      <h3 className="font-semibold text-navy mb-1">{title}</h3>
      <p className="text-sm text-navy-600">{body}</p>
    </div>
  );
}
